import { checkTypeExhausted } from "Utility";
import { Match, matchCompleteByByesOrForfeits } from "model/Match/Match";
import { mapMatchSets } from "./Set/FsMatchSet";
import { Scores } from "model/Scores";
import { MatchSet } from "model/Match/Set/MatchSet";
import { calculateFinalPositions } from "model/Positions";
import { mapMatchFormat } from "./FsMatchFormat";
import { hasSetAdvantage, MatchFormat } from "model/Match/MatchFormat";
import { FsMatch } from "db/model/match/FsMatch";
import {
  getBestPointsDifferential,
  getTeamSetPoints,
} from "model/Match/Set/SetPointRules";
import { mapMatchTeams } from "./FsMatchTeams";
import { getRosterResult } from "model/Results";
import {
  getDefinedRosterIds,
  getDisqualifiedRosterIds,
  getForfeitedRosterIds,
  getNumPlayingRosters,
} from "model/Roster/Roster";
import { getNumTeams } from "model/Match/MatchTeams";
import { SQUAD_SCOPE } from "model/Roster/RosterScope";
import {
  FsSetPoints,
  getRoundPointsBonusRosterId,
} from "db/model/match/FsMatchFormat";

export const mapMatch = (
  fsModel: FsMatch,
  id: string,
  tournamentId: string
): Match | undefined => {
  if (fsModel.ignore) {
    return undefined;
  }

  if (
    !fsModel.numWinners ||
    !fsModel.startTimeMs ||
    !fsModel.format ||
    !fsModel.teams
  ) {
    return undefined;
  }

  const format = mapMatchFormat(fsModel.format);
  if (!format) {
    return undefined;
  }

  const teams = mapMatchTeams(fsModel.teams) ?? {
    scope: SQUAD_SCOPE,
    rosters: [],
  };

  const sets = mapMatchSets(fsModel.sets ?? [], teams, tournamentId, id);
  const scores = calculateMatchScores(
    format,
    sets,
    fsModel.scoreOverrides,
    getDefinedRosterIds(teams.rosters),
    fsModel.format.setPoints
  );

  const isComplete =
    fsModel.overrideComplete ||
    matchCompleteByByesOrForfeits(teams.rosters) ||
    (getNumTeams(teams) > 0 && calculateIsComplete(format, sets, scores));

  // Apply match bonus points only if match complete
  if (isComplete && !!fsModel.format.setPoints?.bonus) {
    const bonusRosterId = getRoundPointsBonusRosterId(
      sets,
      fsModel.format.setPoints.bonus.tie?.num
    );

    if (
      !!bonusRosterId &&
      fsModel.scoreOverrides?.[bonusRosterId] === undefined
    ) {
      scores[bonusRosterId] = (scores[bonusRosterId] ?? 0) + 1;
    }
  }

  const dqRosterIds = getDisqualifiedRosterIds(teams.rosters);
  const ffRosterIds = getForfeitedRosterIds(teams.rosters);

  return {
    id,
    finalPositions: calculateFinalPositions(
      isComplete,
      scores,
      id === "1732942800000x001" || id === "1749272400000x001"
        ? compareScoresAsc
        : compareScores, // todo - extract to not hardcode innit
      getNumPlayingRosters(teams.rosters),
      dqRosterIds,
      fsModel.ffForceLast,
      ffRosterIds,
      fsModel.dqForceLast
    ),
    format,
    isComplete,
    isDelayed: fsModel.isDelayed ?? false,
    numWinners: fsModel.numWinners,
    scores,
    sets,
    startTimeMs: fsModel.startTimeMs,
    teams,
  };
};

// Assumes always single winner for each track worth 1 point
const calculateIsComplete = (
  format: MatchFormat,
  sets: MatchSet[],
  scores: Scores
) => {
  let numCompleteSets = sets.filter(
    (s) => s.isComplete && !s.isTiebreaker
  ).length;

  const formatUsesSetAdvantage = hasSetAdvantage(format);
  if (formatUsesSetAdvantage) {
    numCompleteSets += 1;
  }

  const tiebreakerSet = sets.find((s) => s.isTiebreaker);
  switch (format.type) {
    case "Single": {
      return numCompleteSets >= 1;
    }
    case "BestOf": {
      const bestOf = format.amount;
      const bestOfMinusSetAdv = bestOf - (formatUsesSetAdvantage ? 1 : 0);
      // If no tiebreaker set in format and all regular sets complete, match complete regardless of result
      if (!format.tiebreakerSet && numCompleteSets >= bestOfMinusSetAdv) {
        return true;
      }

      // Add remaining points, exlcuding tiebreaker set
      var remainingPoints = 0;
      for (var i = 0; i < bestOfMinusSetAdv; i++) {
        const { isComplete, points } = sets[i];
        if (isComplete) {
          continue;
        }

        remainingPoints += getBestPointsDifferential(points);
      }

      const scoreValues = Object.values(scores);
      if (scoreValues.length <= 1) {
        return false;
      }

      const bestScore = Math.max(...scoreValues, 0);
      const tiedBest = scoreValues.filter((s) => s === bestScore).length > 1;

      // If no points remaining and tied, complete if not tiebreaker set or tiebreaker set complete
      if (tiedBest && remainingPoints === 0) {
        return !format.tiebreakerSet || (tiebreakerSet?.isComplete ?? false);
      }
      // Else tiedBest and still points up for grabs, match not complete
      else if (tiedBest) {
        return false;
      }

      // Not tiedBest, work out if best is far enough ahead to have already secured win
      const otherScoreValues = scoreValues.filter((s) => s < bestScore);
      const nextBestScore = Math.max(...otherScoreValues, 0);
      return bestScore > nextBestScore + remainingPoints;
    }
    case "FirstTo": {
      // If first to score reached yet match complete, no tiebreaker needed
      const firstTo = format.amount;
      return Math.max(...Object.values(scores), 0) >= firstTo;
    }
    case "PlayAll":
      const playAmount = format.amount;
      // Regular sets not all complete, therefore PlayAll match not finished
      if (numCompleteSets < playAmount) {
        return false;
      }

      // All but tiebreaker played
      // If format not use tiebreaker set, is complete
      if (!format.tiebreakerSet) {
        return true;
      }

      const scoreValues = Object.values(scores);
      const bestScore = Math.max(...scoreValues, 0);
      const tiedBest = scoreValues.filter((s) => s === bestScore).length > 1;
      // Not drawing, therefore tiebreaker not needed
      if (!tiedBest) {
        return true;
      }

      // Tiebreaker needed, check if complete
      return tiebreakerSet?.isComplete ?? false;
    default:
      return checkTypeExhausted(format);
  }
};

const calculateMatchScores = (
  format: MatchFormat,
  sets: MatchSet[],
  scoreOverrides: Scores | undefined,
  rosterIds: string[],
  oldSetPoints: FsSetPoints | undefined
) => {
  // Store match score as set score so that cases like CupMode can still
  // distinguish losing positions from each other for position point purposes
  if (format.type === "Single") {
    return sets.at(0)?.scores ?? {};
  }

  const scores: Scores = {};
  for (let i = 0; i < rosterIds.length; i++) {
    const rosterId = rosterIds[i];
    const scoreOverride = !!scoreOverrides
      ? scoreOverrides[rosterId]
      : undefined;
    if (scoreOverride !== undefined) {
      scores[rosterId] = scoreOverride;
    } else {
      var rosterScore = 0;
      for (let j = 0; j < sets.length; j++) {
        const set = sets[j];
        if (!!set.finalPositions) {
          // todo - temp code
          if (!!oldSetPoints?.points) {
            const amount = oldSetPoints.points.at(j) ?? 1;
            const teamResult = getRosterResult(
              rosterId,
              set.finalPositions,
              set.format.numWinners
            );
            if (teamResult === "D") {
              rosterScore += amount / 2;
            } else if (teamResult === "W") {
              rosterScore += amount;
            }
          } else {
            rosterScore += getTeamSetPoints(
              rosterId,
              set.points,
              set.finalPositions,
              set.format.numWinners
            );
          }
        }
      }

      if (format.setAdvantageRosterIds?.includes(rosterId) ?? false) {
        rosterScore += 1;
      }

      scores[rosterId] = rosterScore;
    }
  }

  return scores;
};

// todo - Add tiebreaker logic here
const compareScores = (scores: Scores, a: string, b: string) => {
  return scores[b] - scores[a];
};

const compareScoresAsc = (scores: Scores, a: string, b: string) => {
  return scores[a] - scores[b];
};
